import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, NavigationExtras, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AlertController, LoadingController, NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { ConnectService } from '../services/connect/connect.service';
import { LoginService } from '../services/login/login.service';
import { MixpanelAnalyticsService } from '../services/mixpanel-analytics/mixpanel-analytics.service';
import { OnboardingService } from '../services/onboarding/onboarding.service';
import { PinCodeService } from '../services/pin-code/pin-code.service';
import { ProfileService } from '../services/profile/profile.service';
import { SentryService, SentrySeverityLevel } from '../services/sentry/sentry.service';
import { StorageService } from '../services/storage/storage.service';
import { ThroughOptionService } from '../services/through-option/through-option.service';

const MAGIC_LINK_TOKEN_KEY = 'magic_link_token';

@Injectable({ providedIn: 'root' })
export class LoginGuard implements CanActivate {
  private router = inject(Router);
  private pincodeService = inject(PinCodeService);
  private loadingCtrl = inject(LoadingController);
  private authService = inject(AuthService);
  private storageService = inject(StorageService);
  private alertCtrl = inject(AlertController);
  private translate = inject(TranslateService);
  private loginService = inject(LoginService);
  private sentryService = inject(SentryService);
  private connectService = inject(ConnectService);
  private profileService = inject(ProfileService);
  private onboardingService = inject(OnboardingService);
  private mixpanel = inject(MixpanelAnalyticsService);
  private navCtrl = inject(NavController);
  private throughOptionService = inject(ThroughOptionService);

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const loginUrl = state.url;

    const searchParams = new URLSearchParams(loginUrl.split('?').pop());
    const isAdminLogin = searchParams.get('admin_login') === 'true';

    this.pincodeService.setAutoLock(false);
    let loading: HTMLIonLoadingElement;

    this.throughOptionService.closeModal();

    const token = await this.authService.readToken();
    if (token && !isAdminLogin) {
      this.pincodeService.setAutoLock(true);
      const lastUsedMagicLinkToken = await this.storageService.get(MAGIC_LINK_TOKEN_KEY);

      if (searchParams.get('token') !== lastUsedMagicLinkToken) {
        await this.showAlreadyLoginAlert();
      }

      await this.profileService.getProfile();
      this.mixpanel.login(this.profileService.getNonSensitiveProfile());

      this.navCtrl.setDirection('root');
      let nextRoute: string = this.onboardingService.getNextRoute();
      if (!nextRoute) {
        nextRoute = 'tabs';
        this.pincodeService.unlock();
      }
      return this.router.parseUrl(nextRoute);
    } else {
      loading = await this.showLoading();

      try {
        const { tenantName, token, isParticipant, magicLinkToken } = await lastValueFrom(this.loginService.verifyLogin(loginUrl));
        this.storageService.remove('lastLoginAttemptCredentials');
        this.storageService.set(MAGIC_LINK_TOKEN_KEY, magicLinkToken);
        return this.router.createUrlTree([tenantName, 'token', token, isParticipant]);
      } catch (e) {
        const error = e as HttpErrorResponse;
        this.sentryService.setMessageForSentryWithErrorInfo('Unknown Error On Login', SentrySeverityLevel.Debug, error as any);
        if (error.status === 404) {
          const navigationExtras: NavigationExtras = {
            queryParams: {
              isVerification: 'true',
            },
          };
          return this.router.createUrlTree(['error/' + 410], navigationExtras);
        }
        if (error.status === 422) {
          this.connectService.handleError(error);
          return false;
        }
        return this.router.parseUrl(`error/${error.status}`);
      } finally {
        loading.dismiss();
      }
    }
  }

  async showAlreadyLoginAlert() {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('ErrorMessages.alreadyLoggedIn'),
      message: this.translate.instant('ErrorMessages.logoutAndTryAgain'),
      buttons: [this.translate.instant('CommonTexts.ok')],
      backdropDismiss: false,
    });
    alert.present();
    await alert.onWillDismiss();
  }

  async showLoading() {
    const loading = await this.loadingCtrl.create({ message: 'Please wait...' });
    loading.present();
    return loading;
  }
}
